import { ApiResponse, ApiError, User, AppConfig } from '@/types';

// Get CSRF token from hidden input field (consistent with CSRFContext)
export const getCSRFToken = (): string | null => {
  const csrfInput = document.querySelector('input[name="csrfmiddlewaretoken"]') as HTMLInputElement;
  return csrfInput?.value || null;
};

// Base API configuration
const API_BASE_URL = '/api';

// Generic fetch wrapper with CSRF token support
export const apiFetch = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const csrfToken = getCSRFToken();
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw {
        error: errorData.error || `HTTP ${response.status}`,
        status: response.status,
        details: errorData,
      } as ApiError;
    }

    const data = await response.json();
    return {
      data,
      status: response.status,
    };
  } catch (error) {
    if (error instanceof TypeError) {
      // Network error
      throw {
        error: 'Network error - please check your connection',
        status: 0,
        details: error,
      } as ApiError;
    }
    throw error;
  }
};

// User API
export const userApi = {
  getCurrentUser: () => apiFetch<User>('/user/'),
  
  updateProfile: (userData: Partial<User>) =>
    apiFetch<User>('/user/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    }),
};

// App configuration API
export const appApi = {
  getConfig: () => apiFetch<AppConfig>('/config/'),
};

// Authentication API (if you add auth endpoints)
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    apiFetch<{ user: User; token?: string }>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
    
  logout: () =>
    apiFetch('/auth/logout/', {
      method: 'POST',
    }),
    
  register: (userData: {
    username: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) =>
    apiFetch<User>('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),
};

// Generic CRUD operations helper
export const createCRUDApi = <T>(endpoint: string) => ({
  list: (params?: Record<string, any>) => {
    const searchParams = params ? `?${new URLSearchParams(params)}` : '';
    return apiFetch<{ results: T[]; count: number; next?: string; previous?: string }>(
      `${endpoint}/${searchParams}`
    );
  },
  
  get: (id: string | number) => apiFetch<T>(`${endpoint}/${id}/`),
  
  create: (data: Partial<T>) =>
    apiFetch<T>(`${endpoint}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    
  update: (id: string | number, data: Partial<T>) =>
    apiFetch<T>(`${endpoint}/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    
  partialUpdate: (id: string | number, data: Partial<T>) =>
    apiFetch<T>(`${endpoint}/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    
  delete: (id: string | number) =>
    apiFetch(`${endpoint}/${id}/`, {
      method: 'DELETE',
    }),
});

// Export the main API object
export const api = {
  user: userApi,
  app: appApi,
  auth: authApi,
  createCRUD: createCRUDApi,
}; 